import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { LocalTipoNovedadService, TipoNovedad } from '../../services/local-tipo-novedad.service';
import { showSpinner, hideSpinner, getErrorMessage } from 'src/app/core/services/functions.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { getEndpoint,getCurrentUser,} from 'src/app/core/services/functions.service';
import { HttpErrorResponse } from '@angular/common/http'; // Import HttpErrorResponse

@Component({
  selector: 'app-tipodenovedades',
  templateUrl: './tipodenovedades.component.html',
  styleUrls: ['./tipodenovedades.component.scss']
})
export class TipodenovedadesComponent implements OnInit {
  displayedColumns: string[] = ['codigo', 'descripcion', 'tipo', 'apartir_de', 'acciones'];
  dataSource = new MatTableDataSource<TipoNovedad>();
  isEditing = false;
  isLoading = false;
  searchControl = new FormControl('');
  tiposNovedad = ['SAP', 'NORMAL'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  novedadForm = new FormGroup({
    id: new FormControl(null),
    codigo: new FormControl('', [Validators.required]),
    tipo: new FormControl('', [Validators.required]),
    descripcion: new FormControl('', [Validators.required]),
    apartir_de: new FormControl(null, [Validators.required, Validators.min(0)]),
    usuario_creacion: new FormControl(''),
    c_emp: new FormControl('')
  });

  constructor(
    private localService: LocalTipoNovedadService,
    private notificationService: NotificationHelper,
    private dialog: MatDialog
  ) {
    // Transformar a mayúsculas los campos de texto
    this.novedadForm.get('codigo')?.valueChanges.subscribe(value => {
      if (value) {
        const upperValue = value.toUpperCase();
        if (upperValue !== value) {
          this.novedadForm.get('codigo')?.setValue(upperValue, { emitEvent: false });
        }
      }
    });

    this.novedadForm.get('descripcion')?.valueChanges.subscribe(value => {
      if (value) {
        const upperValue = value.toUpperCase();
        if (upperValue !== value) {
          this.novedadForm.get('descripcion')?.setValue(upperValue, { emitEvent: false });
        }
      }
    });
  }

  ngOnInit() {
    this.loadData();
    this.setupSearch();
    this.usuarioensession();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  async usuarioensession() {
    const usuario = getCurrentUser();
  }

  async loadData() {
    try {
      showSpinner('Cargando tipos de novedades...');
      const data = await this.localService.getAll().toPromise();
      this.dataSource.data = data;
      //this.notificationService.successMessage('Datos cargados correctamente');
    } catch (error) {
      const message = getErrorMessage(error);
      this.notificationService.errorMessage('Error al cargar los datos');
    } finally {
      hideSpinner();
    }
  }

  async onSubmit() {
    if (this.novedadForm.valid) {
      try {
        showSpinner(this.isEditing ? 'Actualizando tipo de novedad...' : 'Creando tipo de novedad...');
        // Usar getRawValue() en lugar de value para incluir campos deshabilitados
        const formData = this.novedadForm.getRawValue();
        
        // Obtener el usuario actual
        const usuario = getCurrentUser();
        if (!usuario?.usuario_aplicativo || !usuario?.c_emp) {
          this.notificationService.errorMessage('Error: No se pudo obtener la información del usuario. Por favor, inicie sesión nuevamente.');
          hideSpinner();
          return;
        }
        
        if (this.isEditing && formData.id) {
          formData.usuario_modificacion = usuario.usuario_aplicativo;
          formData.c_emp = usuario.c_emp;
          formData.fecha_modificacion = new Date().toISOString();
          
          try {
            await this.localService.validateAndUpdate(formData.id, formData, 'JA').toPromise();
            this.notificationService.successMessage('Tipo de novedad actualizado correctamente');
            this.resetForm();
            this.loadData();
          } catch (error: any) {
            this.notificationService.errorMessage(error.error?.detail || 'Error al actualizar el tipo de novedad');
          }
        } else {
          formData.usuario_creacion = usuario.usuario_aplicativo;
          formData.c_emp = usuario.c_emp;
          formData.fecha_creacion = new Date().toISOString();
          formData.estado = 'ACTIVO';
          
          try {
            await this.localService.validateAndCreate(formData, 'JA').toPromise();
            this.notificationService.successMessage('Tipo de novedad creado correctamente');
            this.resetForm();
            this.loadData();
          } catch (error: any) {
            this.notificationService.errorMessage(error.error?.detail || 'Error al crear el tipo de novedad');
          }
        }
      } catch (error: any) {
        this.notificationService.errorMessage('Error al procesar la solicitud');
      } finally {
        hideSpinner();
      }
    }
  }

  onDelete(novedad: TipoNovedad): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { message: `¿Está seguro que desea eliminar el tipo de novedad ${novedad.codigo}?` }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const usuario = getCurrentUser();
        if (!usuario?.usuario_aplicativo || !usuario?.c_emp) {
          this.notificationService.errorMessage('Error: No se pudo obtener la información del usuario. Por favor, inicie sesión nuevamente.');
          return;
        }

        showSpinner();
        this.localService.delete(novedad.id!, usuario.usuario_aplicativo, usuario.c_emp, 'JA').subscribe({
          next: () => {
            this.notificationService.successMessage('Tipo de novedad eliminado correctamente');
            this.loadData();
          },
          error: (error) => {
            this.notificationService.errorMessage('Error al eliminar el tipo de novedad');
          }
        }).add(() => {
          hideSpinner();
        });
      }
    });
  }

  editarNovedad(novedad: TipoNovedad) {
    this.isEditing = true;
    this.novedadForm.patchValue({
      id: novedad.id,
      codigo: novedad.codigo,
      descripcion: novedad.descripcion,
      tipo: novedad.tipo,
      apartir_de: novedad.apartir_de
    });
    this.novedadForm.get('codigo')?.disable();
  }

  resetForm() {
    this.isEditing = false;
    this.isLoading = false;
    this.novedadForm.reset();
    this.novedadForm.get('codigo')?.enable();
  }

  setupSearch() {
    this.searchControl.valueChanges.subscribe(value => {
      this.applyFilter(value || '');
    });
  }

  applyFilter(filterValue: string) {
    const filter = filterValue.trim().toLowerCase();
    this.dataSource.filterPredicate = (data: TipoNovedad, filter: string) => {
      return data.descripcion.toLowerCase().includes(filter) || 
             data.tipo.toLowerCase().includes(filter);
    };
    this.dataSource.filter = filter;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }
}
